/**
 * "Open in app" for phones. Pure functions only: no imports, no window/document access, so
 * `node --experimental-strip-types tests/app-handoff.test.ts` can run them.
 *
 * Android only for now. iPhone/iPad are deliberately NOT detected (the owner's decision): they get no banner.
 * The Android app (package com.skltd.sksnap) already opens https://sksnap.com/... links (AndroidManifest intent-filter) and
 * maps them to screens in lib/core/routing/app_link_mapper.dart. We hand it the SAME path the browser is showing.
 */

export const ANDROID_PACKAGE = "com.skltd.sksnap";
export const HANDOFF_HOST = "sksnap.com";
/** Query flag added to the fallback URL: "the intent could not be delivered, the app is probably not installed". */
export const APP_MISSING_PARAM = "app";
export const APP_MISSING_VALUE = "missing";
/** Where the "not installed" fallback may send a tester. Empty while the app is only in Play closed testing: then the person simply stays on the page. Set to the public https://play.google.com/store/apps/details?id=com.skltd.sksnap link at public release. */
export const ANDROID_STORE_URL = "";
export const DISMISS_KEY = "sksnap.appBanner.dismissedUntil";
export const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

/** True for a normal browser on an Android PHONE. False for iPhone/iPad, Android tablets, Android WebViews and in-app browsers. */
export function isAndroidPhoneBrowser(ua: string | null | undefined): boolean {
  if (!ua) return false;
  if (!/Android/i.test(ua)) return false;
  if (!/Mobile/i.test(ua)) return false; // Android tablets leave "Mobile" out
  if (/;\s*wv\)|\bwv\b/i.test(ua)) return false; // Android System WebView (another app's embedded page)
  // In-app browsers (Facebook, Messenger, Instagram, Zalo, WeChat, Line, TikTok, Snapchat): they often swallow intent: links.
  if (/FBAN|FBAV|FB_IAB|FBIOS|Messenger|Instagram|Zalo|MicroMessenger|\bLine\/|TikTok|musical_ly|Snapchat/i.test(ua)) return false;
  return true;
}

/** Paths the Android app maps one-to-one (see app_link_mapper.dart). Everything else on an allowed page opens the app home. */
const PASS_THROUGH = ["/u/", "/join/", "/post/", "/reel/", "/hashtag/", "/chat/", "/profile/", "/skai/", "/services/"];
const PASS_THROUGH_EXACT = new Set(["/reels", "/profile", "/snap", "/skai", "/friends", "/notifications", "/channels", "/weather"]);
/** Pages where an "open in app" offer is wrong: policy pages for reviewers/crawlers, account-recovery links the app cannot finish. */
const NEVER = ["/privacy", "/terms", "/cookies", "/child-safety", "/delete-account", "/forgot-password", "/reset-password", "/verify-email", "/auth/qr", "/market", "/admin", "/help"];
/** Full-bleed media screens: the card would sit on top of the video controls. They offer the action from their own menu instead. */
const FULL_BLEED = ["/reels", "/reel/", "/snap"];

const startsWithPath = (p: string, prefix: string) => p === prefix || p.startsWith(prefix.endsWith("/") ? prefix : prefix + "/");

export function isNeverOfferPath(pathname: string): boolean {
  return NEVER.some((n) => startsWithPath(pathname, n));
}
export function isFullBleedPath(pathname: string): boolean {
  return FULL_BLEED.some((n) => startsWithPath(pathname, n));
}

/**
 * The path+query to give the app, or null when the page must not offer it.
 * `/auth` (and other sign-in screens) have no meaning inside the app, so they hand over the page the visitor was trying to reach
 * (`rememberedPath`, saved by ProtectedRoute) or the app home.
 */
export function appPathFor(pathname: string, search: string, rememberedPath?: string | null): string | null {
  if (!pathname.startsWith("/")) return null;
  if (isNeverOfferPath(pathname)) return null;
  if (pathname === "/auth" || pathname === "/forgot-password") {
    return rememberedPath && isSafeAppPath(rememberedPath) ? rememberedPath : "/";
  }
  if (PASS_THROUGH_EXACT.has(pathname) || PASS_THROUGH.some((p) => pathname.startsWith(p))) {
    return pathname + (search && search !== "?" ? search : "");
  }
  return "/";
}

/** Only same-site absolute paths: starts with one "/", no "//", no scheme, no backslash, no control characters, no ";" (intent syntax). */
export function isSafeAppPath(p: string): boolean {
  if (typeof p !== "string" || p.length === 0 || p.length > 512) return false;
  if (!p.startsWith("/") || p.startsWith("//")) return false;
  if (/[\\\u0000-\u001f\u007f;#]/.test(p)) return false;
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(p)) return false;
  return true;
}

/** Drop the "app=missing" flag (and only it) from a query string; keeps every other parameter in order. */
export function stripAppMissingFlag(search: string): { search: string; had: boolean } {
  const params = new URLSearchParams(search);
  const had = params.get(APP_MISSING_PARAM) === APP_MISSING_VALUE;
  if (!had) return { search, had: false };
  params.delete(APP_MISSING_PARAM);
  const rest = params.toString();
  return { search: rest ? `?${rest}` : "", had: true };
}

/** Same page, plus app=missing. This is where Chrome sends the person if no installed app took the intent. */
export function missingFallbackUrl(origin: string, pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.set(APP_MISSING_PARAM, APP_MISSING_VALUE);
  return `${origin}${pathname}?${params.toString()}`;
}

/**
 * Android intent URL. With `package=` set, only the Sksnap app can answer it, so it can never open some other app.
 * Chrome/Samsung Internet/Firefox open the app if installed, otherwise go to S.browser_fallback_url.
 */
export function buildIntentUrl(appPath: string, fallbackUrl: string): string {
  if (!isSafeAppPath(appPath)) appPath = "/";
  return `intent://${HANDOFF_HOST}${appPath}#Intent;scheme=https;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;
}

/** Everything the banner needs to decide, in one place. `now` is milliseconds. */
export function shouldOfferApp(i: {
  ua: string | null | undefined;
  pathname: string;
  standalone: boolean;
  inIframe: boolean;
  dismissedUntil: number;
  now: number;
}): boolean {
  if (i.standalone || i.inIframe) return false;
  if (!isAndroidPhoneBrowser(i.ua)) return false;
  if (i.dismissedUntil > i.now) return false;
  if (isFullBleedPath(i.pathname)) return false;
  return appPathFor(i.pathname, "", null) !== null;
}
